import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormHelperText,
  MenuItem,
  TextField,
  TextFieldProps,
} from '@mui/material';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface Props<T extends { id: number }> {
  name: string;
  label: string;
  options: T[];
  hint?: string;
  textFieldProps?: TextFieldProps;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue: (option: T, value: T) => boolean;
  isOptionDisabled?: (option: T) => boolean;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    state: { selected: boolean }
  ) => React.ReactNode;
  createOption: (input: string) => T;
  validateInput?: (input: string) => boolean;
}

export const CreatableSearchableMultiSelectAutocomplete = <
  T extends { id: number },
>({
  name,
  label,
  options,
  hint,
  textFieldProps,
  getOptionLabel,
  isOptionEqualToValue,
  isOptionDisabled = () => false,
  renderOption,
  createOption,
  validateInput = () => true,
}: Props<T>) => {
  const { control } = useFormContext();
  const [inputValue, setInputValue] = useState('');

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const selectedValues = field.value || [];

        // Merge selected values with provided options, avoiding duplicates
        const mergedOptionsBase = [
          ...selectedValues,
          ...options.filter(
            option =>
              !selectedValues.some((selected: T) =>
                isOptionEqualToValue(selected, option)
              )
          ),
        ];

        // Create a new option if input is valid and not already present
        const specialOption =
          inputValue.trim() &&
          validateInput(inputValue) &&
          !options.find(
            option =>
              getOptionLabel(option).toLowerCase() === inputValue.toLowerCase()
          )
            ? createOption(inputValue)
            : null;

        const mergedOptions = specialOption
          ? mergedOptionsBase.some(option =>
              isOptionEqualToValue(option, specialOption)
            )
            ? mergedOptionsBase
            : [...mergedOptionsBase, specialOption]
          : mergedOptionsBase;

        const isSpecialOption = (option: T) =>
          specialOption && isOptionEqualToValue(option, specialOption);

        // Filter options client-side based on user input
        const filteredOptions = mergedOptions.filter(option =>
          getOptionLabel(option)
            .toLowerCase()
            .includes(inputValue.toLowerCase())
        );

        return (
          <Box>
            {hint && <FormHelperText>{hint}</FormHelperText>}
            <Autocomplete
              multiple
              options={filteredOptions}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue}
              value={selectedValues}
              onChange={(event, newValue) => {
                field.onChange(newValue);
                if (
                  specialOption &&
                  newValue.some(option => isSpecialOption(option))
                ) {
                  setInputValue(''); // Clear input after adding a new option
                }
              }}
              inputValue={inputValue}
              onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
              disableCloseOnSelect
              getOptionDisabled={isOptionDisabled}
              renderInput={params => (
                <TextField
                  {...params}
                  label={label}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  {...textFieldProps}
                />
              )}
              renderOption={
                renderOption ||
                ((props, option, { selected }) => {
                  const label = isSpecialOption(option)
                    ? `"${getOptionLabel(option)}" Hinzufügen`
                    : getOptionLabel(option);
                  return (
                    <MenuItem dense {...props} key={option.id}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlank fontSize='small' />}
                        checkedIcon={<CheckBoxIcon fontSize='small' />}
                        sx={{ marginRight: 1 }}
                        checked={selected}
                        disabled={isOptionDisabled(option)}
                      />
                      {label}
                    </MenuItem>
                  );
                })
              }
            />
          </Box>
        );
      }}
    />
  );
};
